// One upcoming training occurrence (see `features/athlete-clubs.md`), shared by
// the clubs list (which shows only the next one) and the club page (which shows
// the fortnight).
//
// Two states worth distinguishing, and the copy says which is which: an
// occurrence a coach has already created the dated session row for is
// *confirmed*, while one derived purely from the class's weekly pattern is the
// *usual time* — most clubs only create the row when they take attendance, so
// the second is the common case and must not read as uncertainty about whether
// training happens.

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { useTranslation } from '../../state/I18nContext';
import { useMemo } from 'react';
import { isoDateOf } from '../../domain';
import type { UpcomingSession } from '../../domain';

interface UpcomingSessionRowProps {
  session: UpcomingSession;
  /** Emphasise it — used for the very next occurrence. */
  highlight?: boolean;
  /** Show which club it belongs to (for a cross-club list). */
  showClub?: boolean;
}

export function UpcomingSessionRow({
  session,
  highlight = false,
  showClub = false,
}: UpcomingSessionRowProps) {
  const { t, localeCode } = useTranslation();

  const dateFormat = useMemo(
    () =>
      new Intl.DateTimeFormat(localeCode, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        timeZone: 'UTC',
      }),
    [localeCode],
  );

  // "Today" / "Tomorrow" read far better than a date for the next session, which
  // is the whole point of highlighting it.
  const today = isoDateOf(new Date());
  const relative =
    session.date === today
      ? t('athlete.upcoming.today')
      : session.date === isoDateOf(new Date(Date.now() + 86_400_000))
        ? t('athlete.upcoming.tomorrow')
        : null;

  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{
        p: highlight ? 1.5 : 0,
        borderRadius: highlight ? 1 : 0,
        // A tinted panel, not a solid one: the row sits inside cards of both
        // themes and must not fight either.
        bgcolor: highlight ? 'action.selected' : 'transparent',
      }}
    >
      <EventAvailableIcon color={highlight ? 'primary' : 'disabled'} fontSize="small" />
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Typography variant={highlight ? 'subtitle2' : 'body2'} noWrap>
          {session.className}
          {showClub && ` · ${session.clubName}`}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {relative ??
            dateFormat.format(new Date(`${session.date}T00:00:00Z`))}
          {` · ${session.startTime}–${session.endTime}`}
        </Typography>
      </Box>
      {session.scheduled ? (
        <Chip size="small" color="success" variant="outlined" label={t('athlete.upcoming.confirmed')} />
      ) : (
        <Chip size="small" variant="outlined" label={t('athlete.upcoming.usualTime')} />
      )}
    </Stack>
  );
}
